/**
 * Terrain fog at layer 0, made cheap (decision 104: keep terrain fog at L0, "maybe expensive", so per team or on demand).
 *
 * The turn clock gives every unit its own fog and reveals to the horizon over every planet, copying the fog's arrays
 * on each update (~2.4 ms a unit on a megaplanet, ~1.2 MB a unit). On the action clock that was allocated for every
 * unit at setup and never read again: 58 MB of dead weight at 25 a side. Here instead:
 *
 * - **One fog per team.** Every unit's slot in `state.fog` points at its team's fog, so lookups by player index keep
 *   working and the memory is per team.
 * - **What a person sees:** the unit's own planet, within its sight radius (the action bot's: the weather's limit at
 *   the person scale, capped near the horizon), with terrain line of sight from the eye. Live at human scale only
 *   (liveTeamFog); at classic scale the team fog is revealed once at setup.
 * - **Only when something changed:** a unit reveals on the action bot's sight beat, and only once it has moved a share
 *   of its sight radius since its last reveal (its anchor, kept in the fog so a snapshot carries it); at most
 *   L0_FOG_MAX_PER_BEAT units a beat, round-robin, within a budget of line-of-sight tests (L0_FOG_BEAT_TESTS).
 * - **Only the cells in sight:** a reveal visits the vertices filed in the cube-sphere cells under its sight cap.
 * - **In place**, as decision 37 allows for the action clock's hot state, with the per-tick checksum's cached hashes
 *   invalidated (core/contentHash.ts).
 *
 * Pure game logic: no Three.js; deterministic (dmath, no wall clock).
 */
import type { FogState, PlanetState, PlayerState, GameState, Vec3 } from '../types';
import { initializeFog } from './fog';
import { losEyePoint, hasLineOfSight } from './los';
import { contentChanged } from './contentHash';
import { personSetOf } from './scaleProfile';
import { weatherEffectHere, weatherEffect } from './weather';
import { PHYS_DT } from '../constants';
import { buildCellIndex, queryCellIndexCount, cellQueryIds, cellsPerEdge, type CellIndex } from './cells';

/** Ticks between reveals: the action bot's sight beat (5 Hz). */
export const L0_FOG_PERIOD = 24;
/** A unit reveals again once its eye has moved this share of its sight radius since its last reveal. */
export const L0_FOG_STEP = 0.25;
/** The reveal radius is capped at this many horizon distances from the eye (hills beyond the horizon still show). */
export const L0_FOG_HORIZONS = 2;
/** A vertex is sighted at this height above the ground, in person eye heights, so its own terrain doesn't hide it. */
const TARGET_LIFT = 0.25;
/** At most this many units reveal on one beat (round-robin from the beat's number), so a beat never spikes the tick. */
export const L0_FOG_MAX_PER_BEAT = 8;
/**
 * A work budget per beat, in line-of-sight tests (a count, never a time: the same on every machine). A beat stops
 * taking units once it is spent, after at least one. At human scale it never binds (~100 tests a unit); at classic
 * scale a person sees ~1,500 vertices, so a beat takes about one unit.
 */
export const L0_FOG_BEAT_TESTS = 1200;

/**
 * The planet's vertices filed by cube-sphere cell (core/cells.ts), so a reveal visits only the cells within sight.
 * A cache derived from the planet's base positions (they never change), kept outside the state.
 */
const vertexCells = new WeakMap<Float32Array, CellIndex>();
function vertexCellsOf(planet: PlanetState): CellIndex {
  let idx = vertexCells.get(planet.basePositions);
  if (!idx) {
    const b = planet.basePositions;
    const count = planet.heightmap.length;
    // cells about two vertex spacings across: spacing ≈ the sphere's area shared by its vertices
    const spacing = Math.sqrt((4 * Math.PI * planet.radius * planet.radius) / Math.max(1, count));
    idx = buildCellIndex(count, i => {
      const x = b[i * 3], y = b[i * 3 + 1], z = b[i * 3 + 2];
      const l = Math.sqrt(x * x + y * y + z * z);
      return { x: x / l, y: y / l, z: z / l };
    }, cellsPerEdge(planet.radius, spacing * 2));
    vertexCells.set(planet.basePositions, idx);
  }
  return idx;
}

/** One fog per team; `fog[i]` is player i's team's fog (shared object). Turn-clock code keeps its per-player shape. */
export function teamFogs(players: ReadonlyArray<Pick<PlayerState, 'teamIndex'>>, planets: PlanetState[]): FogState[] {
  const teams = [...new Set(players.map(p => p.teamIndex))].sort((a, b) => a - b);
  const byTeam = new Map<number, FogState>();
  const fresh = initializeFog(teams.length, planets);
  teams.forEach((t, i) => byTeam.set(t, { ...fresh[i], anchors: {} }));
  return players.map(p => byTeam.get(p.teamIndex)!);
}

/** The radius a unit reveals: its sight (the weather's limit, person scale), capped near its horizon. */
export function l0SightRadius(state: GameState, unit: PlayerState, tick: number): number {
  const ps = personSetOf(state);
  const sight = weatherEffectHere(state, unit.planetIndex, unit.surfaceDir, tick * PHYS_DT).sight * ps.k;   // 62.54: where the unit is
  return Math.min(sight, l0Horizon(state, unit));
}

/** The horizon cap on a unit's reveal (L0_FOG_HORIZONS of its eye's horizon distance). */
export function l0Horizon(state: GameState, unit: PlayerState): number {
  const ps = personSetOf(state);
  const planet = state.planets[unit.planetIndex];
  return Math.sqrt(2 * planet.radius * Math.max(ps.losEyeHeight, 1e-6)) * L0_FOG_HORIZONS;
}

/**
 * Reveal into `fog` the vertices of the unit's planet within `radius` of `eye` that the eye can see. In place.
 * Returns how many vertices it revealed.
 */
/** 62.57: the vertex candidates under a sight cap, reused (filled and read within one reveal). */
let fogCandidates = new Uint32Array(1024);
export function revealFromEye(fog: FogState, eye: Vec3, planetIndex: number, planets: PlanetState[], colors: Float32Array | undefined, radius: number, lift: number, revealTurn: number): { revealed: number; tests: number } {
  const planet = planets[planetIndex];
  const fp = fog.planets[planetIndex];
  if (!planet?.alive || !fp) return { revealed: 0, tests: 0 };
  let tests = 0;
  const r2 = radius * radius;
  const cx = planet.position.x, cy = planet.position.y, cz = planet.position.z;
  let revealed = 0;
  // the candidates: vertices in the cells under the sight cap (the angle the radius spans on the sphere, with slack
  // for the eye and the terrain standing above the sphere); ascending, so the reveal order is the plain loop's
  const ex = eye.x - cx, ey = eye.y - cy, ez = eye.z - cz;
  const el = Math.sqrt(ex * ex + ey * ey + ez * ez);
  const angle = Math.min(Math.PI, radius / Math.max(1e-9, planet.radius) * 1.25 + 0.01);
  // (62.57: from the cells' buffer into the fog's own, ascending as queryCellIndex gives them)
  const count = queryCellIndexCount(vertexCellsOf(planet), { x: ex / el, y: ey / el, z: ez / el }, angle, true);
  if (count > fogCandidates.length) fogCandidates = new Uint32Array(Math.max(count, 2 * fogCandidates.length));
  fogCandidates.set(cellQueryIds().subarray(0, count));
  for (let ci = 0; ci < count; ci++) {
    const i = fogCandidates[ci];
    if (fp.revealedVertices[i]) continue;
    const bx = planet.basePositions[i * 3], by = planet.basePositions[i * 3 + 1], bz = planet.basePositions[i * 3 + 2];
    const bLen = Math.sqrt(bx * bx + by * by + bz * bz);
    const nx = bx / bLen, ny = by / bLen, nz = bz / bLen;
    const h = planet.heightmap[i];
    const vx = cx + bx + nx * h, vy = cy + by + ny * h, vz = cz + bz + nz * h;
    const dx = vx - eye.x, dy = vy - eye.y, dz = vz - eye.z;
    if (dx * dx + dy * dy + dz * dz > r2) continue;
    const target = { x: vx + nx * lift, y: vy + ny * lift, z: vz + nz * lift };
    tests++;
    if (!hasLineOfSight(eye, target, planets)) continue;
    fp.revealedVertices[i] = true;
    fp.terrainSnapshot[i] = h;
    if (colors) { fp.colorSnapshot[i * 3] = colors[i * 3]; fp.colorSnapshot[i * 3 + 1] = colors[i * 3 + 1]; fp.colorSnapshot[i * 3 + 2] = colors[i * 3 + 2]; }
    fp.revealTurn[i] = revealTurn;
    revealed++;
  }
  // in-place edits: the per-tick checksum's cached hashes of these arrays are stale now
  if (revealed > 0) contentChanged(fp.revealedVertices, fp.terrainSnapshot, fp.colorSnapshot, fp.revealTurn);
  return { revealed, tests };
}

/**
 * The action clock's fog step: on the sight beat, every living, grounded unit that has moved far enough since its last
 * reveal (or never revealed) reveals into its team's fog and moves its anchor. In place; returns vertices revealed.
 * `force` reveals for every unit regardless of the beat or the anchor (setup, a respawn wave).
 */
/**
 * Live person-sight fog runs where the observer is a person smaller than the unit (the human scale: layer 0 under the
 * one-world ruling). At classic scale a person's eye stands as high as the vehicle's and sees ~22 u, so a raycast per
 * vertex costs a quarter to half of a whole skirmish (measured); there the team fog is revealed once at setup by the
 * turn clock's horizon rule and not again in play.
 */
export function liveTeamFog(state: Pick<GameState, 'config'>): boolean {
  return personSetOf(state).k < 1;
}

export function stepTeamFog(state: GameState, colorsFor: (planetIndex: number) => Float32Array | undefined, tick: number, force = false): number {
  if (!liveTeamFog(state)) return 0;
  if (!force && tick % L0_FOG_PERIOD !== 0) return 0;
  const ps = personSetOf(state);
  let total = 0;
  let reveals = 0;
  let tests = 0;
  const count = state.players.length;
  const first = count > 0 ? Math.floor(tick / L0_FOG_PERIOD) % count : 0;   // round-robin start, so no unit is always last
  for (let k = 0; k < count; k++) {
    if (!force && (reveals >= L0_FOG_MAX_PER_BEAT || (reveals > 0 && tests >= L0_FOG_BEAT_TESTS))) break;
    const i = (first + k) % count;
    const unit = state.players[i];
    const fog = state.fog[i];
    if (!unit.alive || unit.airborne || !fog) continue;
    const anchors = fog.anchors ?? (fog.anchors = {});
    const eye = losEyePoint(unit, state.planets, ps.losEyeHeight);
    const radius = l0SightRadius(state, unit, tick);
    const last = anchors[unit.id];
    if (!force && last) {
      const dx = eye.x - last.x, dy = eye.y - last.y, dz = eye.z - last.z;
      const step = radius * L0_FOG_STEP;
      if (dx * dx + dy * dy + dz * dz < step * step) continue;
    }
    const r = revealFromEye(fog, eye, unit.planetIndex, state.planets, colorsFor(unit.planetIndex), radius, ps.losEyeHeight * TARGET_LIFT, state.turnNumber);
    total += r.revealed;
    tests += r.tests;
    anchors[unit.id] = eye;
    reveals++;
  }
  return total;
}
